import { useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductionDeclarationMaterialCommentsSection } from '@/features/production-declaration/components/statement/production-declaration-material-comments-section';
import { ProductionDeclarationMaterialCommodityOriginCard } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origin-card';
import { ProductionDeclarationMaterialPlacesList } from '@/features/production-declaration/components/statement/production-declaration-material-places-list';
import { ProductionPlaceDialog } from '@/features/production-declaration/components/statement/production-place/production-place-dialog';
import { useCanEditProductionDeclaration } from '@/features/production-declaration/hooks/use-can-edit-production-declaration';
import { useProductionDeclarationCorrectionRequestComment } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment';
import { useGetProductionDeclarationCommodityOriginProductionPlaces } from '@/services/apis/queries/production-declaration/use-get-production-declaration-commodity-origin-production-places';
import { CorrectionRequestEntityType } from '@/types/core/purchase-order/correction-request';
import type { ProductionDeclarationStatusCode as ProductionDeclarationStatusCodeType } from '@/types/core/purchase-order/production-declaration-status-code';

type ProductionDeclarationMaterialPlacesCardProps = {
  canComment?: boolean;
  commodityOriginId: string;
  showSupplierCorrectionComments?: boolean;
  statusCode: ProductionDeclarationStatusCodeType;
};

export function ProductionDeclarationMaterialPlacesCard({
  canComment = false,
  commodityOriginId,
  showSupplierCorrectionComments = false,
  statusCode,
}: ProductionDeclarationMaterialPlacesCardProps) {
  const { id, materialId } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const [isProductionPlaceDialogOpen, setIsProductionPlaceDialogOpen] =
    useState(false);
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const productionDeclarationId = id ? `${id}` : '';
  const resolvedMaterialId = materialId ?? '';
  const { comments, create, edit, remove, resolution } =
    useProductionDeclarationCorrectionRequestComment({
      productionDeclarationId,
      materialId: resolvedMaterialId,
      commodityOriginId,
      entityType: CorrectionRequestEntityType.ProductionPlaces,
      token,
    });
  const canEdit = useCanEditProductionDeclaration(statusCode);
  const canManageProductionPlaces =
    canEdit &&
    Boolean(productionDeclarationId && resolvedMaterialId && commodityOriginId);
  const { data: productionPlaces = [], isLoading } =
    useGetProductionDeclarationCommodityOriginProductionPlaces(
      productionDeclarationId,
      resolvedMaterialId,
      commodityOriginId,
      token,
    );
  const productionPlaceGuidelines = [
    t(
      'materialCommodityOrigins.cards.productionPlace.guidelines.multiplePlots',
    ),
    t(
      'materialCommodityOrigins.cards.productionPlace.guidelines.reportSeparately',
    ),
  ];

  return (
    <ProductionDeclarationMaterialCommodityOriginCard
      actionDisabled={!canManageProductionPlaces}
      actionLabel={t('materialCommodityOrigins.cards.productionPlace.cta')}
      description={t(
        productionPlaces.length === 0
          ? 'materialCommodityOrigins.cards.productionPlace.empty'
          : '',
      )}
      onActionClick={() => {
        setIsProductionPlaceDialogOpen(true);
      }}
      onCommentClick={canComment ? create.open : undefined}
      title={t('materialCommodityOrigins.cards.productionPlace.title')}
      tooltipAriaLabel={t(
        'materialCommodityOrigins.cards.productionPlace.description',
      )}
      tooltipContent={
        <>
          <p className='font-semibold text-declaration-foreground text-sm'>
            {t('materialCommodityOrigins.cards.productionPlace.title')}
          </p>
          <p>
            {t('materialCommodityOrigins.cards.productionPlace.description')}
          </p>
          <ul className='list-disc space-y-1 pl-5'>
            {productionPlaceGuidelines.map((guideline) => (
              <li key={guideline}>{guideline}</li>
            ))}
          </ul>
          <p>
            {t(
              'materialCommodityOrigins.cards.productionPlace.guidelines.compositeProducts',
            )}
          </p>
          <p>
            {t(
              'materialCommodityOrigins.cards.productionPlace.guidelines.compositeProductsExample',
            )}
          </p>
        </>
      }
    >
      {isProductionPlaceDialogOpen ? (
        <ProductionPlaceDialog
          canSubmit={canManageProductionPlaces}
          commodityOriginId={commodityOriginId}
          materialId={resolvedMaterialId}
          onOpenChange={setIsProductionPlaceDialogOpen}
          open={isProductionPlaceDialogOpen}
          productionDeclarationId={productionDeclarationId}
          token={token}
        />
      ) : null}

      <div className='space-y-6'>
        <ProductionDeclarationMaterialCommentsSection
          canComment={canComment}
          comments={comments}
          create={create}
          edit={edit}
          remove={remove}
          resolution={resolution}
          showSupplierCorrectionComments={showSupplierCorrectionComments}
        />
        <div className='max-w-full overflow-x-auto'>
          <ProductionDeclarationMaterialPlacesList
            commodityOriginId={commodityOriginId}
            isLoading={isLoading}
            materialId={resolvedMaterialId}
            productionDeclarationId={productionDeclarationId}
            productionPlaces={productionPlaces}
            statusCode={statusCode}
            token={token}
          />
        </div>
      </div>
    </ProductionDeclarationMaterialCommodityOriginCard>
  );
}
